/* eslint-disable @typescript-eslint/no-use-before-define */

import { isNotUndefined } from '@technically/is-not-undefined';
var KNOWN_SCRIPTS = ['hant', 'hans'];
var KNOWN_RTL_LANGUAGES = ['ar', 'he', 'ur'];
var LANG_CODE_REGEX = /^[a-z]{2,3}$/;
var CACHE = new Map();
export var Locale;
(function (_Locale) {
  /**
   * Shortest possible slug that can identify a locale for the given newsroom.
   * This can be full language code or lowercase region code if they're unanbiguously identify a locale
   * or full locale slug if nothing else applies.
   */

  function from(locale) {
    if (typeof locale === 'object') {
      return locale;
    }
    var cached = CACHE.get(locale);
    if (cached) {
      return cached;
    }
    var [lang, ...rest] = locale.toLowerCase().split(/[_-]/g);
    if (!lang.match(LANG_CODE_REGEX)) {
      throw new Error("Invalid locale code provided: \"".concat(locale, "\"."));
    }
    var scripts = rest.filter(s => KNOWN_SCRIPTS.includes(s));
    var regions = rest.filter(s => !KNOWN_SCRIPTS.includes(s)).map(region => region.toUpperCase());
    if (scripts.length > 1) {
      throw new Error("Unable to parse the provided locale code: \"".concat(locale, "\". Multiple script codes detected."));
    }
    if (regions.length > 1) {
      throw new Error("Unable to parse the provided locale code: \"".concat(locale, "\". Multiple region codes detected."));
    }
    var scriptCode = scripts[0];
    var regionCode = regions[0];
    var code = [lang.toLowerCase(), regionCode, scriptCode ? toPascalCase(scriptCode) : undefined].filter(isNotUndefined).join('_');
    var isoCode = code.replace('_', '-');
    var obj = {
      code,
      isoCode,
      slug: isoCode.toLowerCase(),
      direction: KNOWN_RTL_LANGUAGES.includes(lang) ? 'rtl' : 'ltr',
      lang,
      region: regionCode,
      script: scriptCode ? toPascalCase(scriptCode) : undefined
    };
    CACHE.set(obj.code, obj);
    CACHE.set(obj.isoCode, obj);
    CACHE.set(obj.slug, obj);
    return obj;
  }
  _Locale.from = from;
  function isValid(locale) {
    try {
      Locale.from(locale);
    } catch (_unused) {
      return false;
    }
    return true;
  }
  _Locale.isValid = isValid;
  function isLanguageCode(locale, langCode) {
    return Locale.from(locale).lang === langCode.toLowerCase();
  }
  _Locale.isLanguageCode = isLanguageCode;
  function isRegionCode(locale, regionCode) {
    return Locale.from(locale).region === regionCode.toUpperCase();
  }
  _Locale.isRegionCode = isRegionCode;
  function isRegionIndependent(locale) {
    return !Locale.from(locale).region;
  }
  _Locale.isRegionIndependent = isRegionIndependent;
  function isEqual(a, b) {
    var al = Locale.from(a);
    var bl = Locale.from(b);
    return al.lang === bl.lang && al.region === bl.region && al.script === bl.script;
  }
  _Locale.isEqual = isEqual;
  function isSameLang(a, b) {
    return Locale.from(a).lang === Locale.from(b).lang;
  }
  _Locale.isSameLang = isSameLang;
  function isSameRegion(a, b) {
    var al = Locale.from(a);
    var bl = Locale.from(b);
    if (!al.region || !bl.region) {
      // It's never the same region, if it's not defined.
      return false;
    }
    return al.region === bl.region;
  }
  _Locale.isSameRegion = isSameRegion;
})(Locale || (Locale = {}));
function toPascalCase(input) {
  return "".concat(input.substring(0, 1).toUpperCase()).concat(input.substring(1).toLowerCase());
}